/**
 * Login-Vollbildseite für das bidi-handel Deployment (2026-06-24).
 *
 * Single-User-Login (Benutzername + Passwort). Dark-Ops-Theme analog zum
 * Rest der App (theme.css-Variablen, .input/.btn-Klassen). Keine Selbst-
 * Registrierung, kein Passwort-Reset — bewusst minimalistisch für genau
 * einen User. Nach erfolgreichem Login lädt `<AuthGate>` automatisch die App.
 */

import { useState, type FormEvent } from "react";
import { useAuth } from "./AuthContext";
import { Icon } from "../shell/Icon";

export function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await login(username.trim(), password);
    setSubmitting(false);
    if (!result.ok) setError(result.error);
    // Erfolg: AuthGate rendert die App neu — kein manueller Redirect nötig.
  };

  return (
    <>
      <div className="app-bg" />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          className="surface fade-up"
          style={{ width: "100%", maxWidth: 380, padding: 32 }}
        >
          {/* Logo + Marke */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <Icon name="logo" size={30} />
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--fg)",
                  letterSpacing: "-0.01em",
                }}
              >
                bidi·handel
              </div>
              <div
                className="uppercase-mini"
                style={{ letterSpacing: "0.18em" }}
              >
                INNENDIENST
              </div>
            </div>
          </div>

          <h1
            style={{
              fontSize: 19,
              fontWeight: 600,
              color: "var(--fg)",
              margin: "0 0 4px",
              letterSpacing: "-0.01em",
            }}
          >
            Anmelden
          </h1>
          <p
            style={{
              fontSize: 12.5,
              color: "var(--fg-dim)",
              lineHeight: 1.5,
              margin: "0 0 22px",
            }}
          >
            Anfrage- &amp; Angebotsbearbeitung für Baustoffhändler.
          </p>

          <form
            onSubmit={onSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: "var(--fg-mute)",
                }}
              >
                Benutzername
              </label>
              <input
                className="input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: "var(--fg-mute)",
                }}
              >
                Passwort
              </label>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <div
                style={{
                  fontSize: 12,
                  padding: "8px 12px",
                  borderRadius: "var(--r)",
                  background: "var(--bad-tint)",
                  border: "1px solid var(--bad)",
                  color: "var(--bad)",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn primary"
              disabled={submitting || !username || !password}
              style={{ justifyContent: "center", marginTop: 2 }}
            >
              {submitting ? "Wird angemeldet …" : "Anmelden"}
            </button>
          </form>

          <div
            style={{
              marginTop: 22,
              paddingTop: 16,
              borderTop: "1px solid var(--line-soft)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 10.5, color: "var(--fg-faint)" }}>
              Single-User-Zugang
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
